/**
 * Dialogue Modal Component - React component for NPC interactions
 * Displays dialogue tree and handles player choices
 */

import React, { useState, useEffect } from 'react';
import { RailwayNPCData } from '@/services/railwayWorldStateService';

export interface DialogueNode {
  id: string;
  text: string;
  speaker: string;
  choices?: DialogueChoice[];
  karmaImpact?: number;
}

export interface DialogueChoice {
  id: string;
  text: string;
  nextNodeId?: string;
  karmaImpact?: number;
  condition?: () => boolean;
}

interface DialogueModalProps {
  npc: RailwayNPCData;
  isOpen: boolean;
  onClose: () => void;
  onChoice?: (choice: DialogueChoice) => void;
}

export const DialogueModal: React.FC<DialogueModalProps> = ({
  npc,
  isOpen,
  onClose,
  onChoice,
}) => {
  const [currentNode, setCurrentNode] = useState<DialogueNode | null>(null);
  const [dialogueHistory, setDialogueHistory] = useState<DialogueNode[]>([]);

  useEffect(() => {
    if (isOpen && npc) {
      // Initialize dialogue with greeting
      const greeting: DialogueNode = {
        id: 'greeting',
        text: `Hello, I'm ${npc.name}. How can I help you?`,
        speaker: npc.name,
        choices: [
          {
            id: 'ask_about_archetype',
            text: `Tell me about your archetype (${npc.archetype})`,
            karmaImpact: 0,
          },
          {
            id: 'ask_about_karma',
            text: `What's your karma? (${npc.karma > 0 ? 'Positive' : npc.karma < 0 ? 'Negative' : 'Neutral'})`,
            karmaImpact: npc.karma > 0 ? 1 : npc.karma < 0 ? -1 : 0,
          },
          {
            id: 'goodbye',
            text: 'Goodbye',
            karmaImpact: 0,
          },
        ],
      };
      setCurrentNode(greeting);
      setDialogueHistory([greeting]);
    }
  }, [isOpen, npc]);

  if (!isOpen || !npc) return null;

  const handleChoice = (choice: DialogueChoice) => {
    if (choice.nextNodeId) {
      // Load next node
      // For now, just handle goodbye
      if (choice.id === 'goodbye') {
        onClose();
        return;
      }

      // Generate response based on choice
      let responseText = '';
      if (choice.id === 'ask_about_archetype') {
        responseText = `I'm a ${npc.archetype}. It defines who I am in this world.`;
      } else if (choice.id === 'ask_about_karma') {
        responseText = `My karma is ${npc.karma}. ${npc.karma > 0 ? 'I try to help others.' : npc.karma < 0 ? 'I look out for myself.' : 'I stay neutral.'}`;
      }

      const responseNode: DialogueNode = {
        id: `response_${choice.id}`,
        text: responseText,
        speaker: npc.name,
        choices: [
          {
            id: 'continue',
            text: 'Continue',
            karmaImpact: 0,
          },
          {
            id: 'goodbye',
            text: 'Goodbye',
            karmaImpact: 0,
          },
        ],
      };

      setCurrentNode(responseNode);
      setDialogueHistory((prev) => [...prev, responseNode]);

      if (onChoice) {
        onChoice(choice);
      }
    } else if (choice.id === 'continue') {
      // Return to previous node or close
      if (dialogueHistory.length > 1) {
        setCurrentNode(dialogueHistory[dialogueHistory.length - 2]);
        setDialogueHistory((prev) => prev.slice(0, -1));
      }
    } else if (choice.id === 'goodbye') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-slate-900 border border-cyan-500/40 rounded-xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-full ${
                npc.karma > 0
                  ? 'bg-green-500/20 border-green-500'
                  : npc.karma < 0
                  ? 'bg-red-500/20 border-red-500'
                  : 'bg-gray-500/20 border-gray-500'
              } border-2 flex items-center justify-center text-lg font-bold`}
            >
              {npc.name.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-cyan-300">{npc.name}</h2>
              <p className="text-sm text-slate-400">{npc.archetype}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
            aria-label="Close dialogue"
          >
            ✕
          </button>
        </div>

        {/* Dialogue History */}
        <div className="max-h-64 overflow-y-auto mb-4 space-y-3">
          {dialogueHistory.map((node) => (
            <div key={node.id} className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-sm text-slate-300">{node.text}</p>
            </div>
          ))}
        </div>

        {/* Current Node Choices */}
        {currentNode && currentNode.choices && currentNode.choices.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-slate-400 mb-2">Choose an option:</p>
            {currentNode.choices.map((choice) => (
              <button
                key={choice.id}
                onClick={() => handleChoice(choice)}
                className="w-full text-left px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg transition-colors text-cyan-300"
              >
                {choice.text}
                {choice.karmaImpact !== undefined && choice.karmaImpact !== 0 && (
                  <span className="text-xs text-slate-400 ml-2">
                    ({choice.karmaImpact > 0 ? '+' : ''}
                    {choice.karmaImpact} karma)
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

